import React from 'react'
import { LensedAlert } from '@grammarly/http-clients-capi'
import { Icon, Flex, InterfaceOkIcon, Text } from '@grammarly/design-system'
import { FormattedMessage, useIntl } from 'react-intl'

import { getTotalErrors, parseAlerts } from '@components/section/text-checker/report-flow/report-summary/helpers'
import { FeatureErrorsType } from '@components/section/text-checker/report-flow/report-summary/types'
import { Counter } from '@components/section/text-checker/report-flow/report-summary/counter'
import { ReportSummaryCircle } from '@components/section/text-checker/report-flow/report-summary/icons'
import { PlagiarismChartDescriptionCopy } from '@components/section/text-checker/report-flow/report-summary/chart-description'
import { PlagiarismHelper } from '@components/section/text-checker/report-flow/report-summary/helpers/plagiarism-helper'
import { useMediaQuery } from '@lib/hooks/media-query'
import { useTool } from '@components/tool/tool-context'
import { smallFoldHeight } from '@components/tool/constants'

import { ToolOutputContentProps } from '../../types'
import { ToolLoading } from '../tool-loading'

import style from './plagiarism-checker.module.scss'

export const PlagiarismChecker: React.FC<ToolOutputContentProps> = ({ isLoading, outputContent }) => {
  if (isLoading) {
    return <ToolLoading />
  }

  if (outputContent) {
    return outputContent
  }

  return <PlagiarismCheckerOutput />
}

interface PlagiarismCheckerOutputProps {
  response?: LensedAlert[]
}

interface FeatureErrorProps {
  isInitial: boolean
  error: FeatureErrorsType
}

const Feature: React.FC<FeatureErrorProps> = ({ isInitial, error }) => {
  const intl = useIntl()
  const { feature, total } = error
  const isPlagiarism = feature.alertName === 'Originality'

  return (
    <li className={style.reportListItem}>
      <Text variant="text-xsmall" as="span">
        {feature.title}
      </Text>
      {isInitial ? (
        <Text variant="text-xsmall" as="span">
          {
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            '–––'
          }
        </Text>
      ) : total > 0 ? (
        <div className={style.alertCount}>
          <Counter
            multiplier={2.5}
            alertCount={isPlagiarism ? '!' : total}
            ariaHidden={isPlagiarism}
            color="whiteOnCritical"
          />
        </div>
      ) : (
        <Icon
          icon={InterfaceOkIcon}
          accessibilityLabel={intl.formatMessage(
            {
              id: 'tool.output.plagiarismChecker.feature.icon.accessibilityLabel',
              description: 'Tool - Output - Plagiarism Checker - Feature - Icon - Accessibility Label',
              defaultMessage: 'No errors for {featureTitle}',
            },
            { featureTitle: feature.title }
          )}
          size="large"
        />
      )}
    </li>
  )
}

interface PlagiarismTitleProps {
  isInitial: boolean
  errors: FeatureErrorsType[]
  isSmallerFoldHeight: boolean
}

const PlagiarismTitle: React.FC<PlagiarismTitleProps> = ({ isInitial, errors, isSmallerFoldHeight }) => {
  const { submitButtonText } = useTool()

  const plagiarismHelper = new PlagiarismHelper(errors)
  const totalErrors = getTotalErrors(errors)

  if (isInitial) {
    return (
      <Text variant="text-medium" as="p">
        <FormattedMessage
          id="tool.output.plagiarismChecker.title.copy"
          description="Tool - Output - Plagiarism Checker - Title - Copy"
          defaultMessage="Add your text and click {submitButtonText} to see your results."
          values={{ submitButtonText }}
        />
      </Text>
    )
  }

  return (
    <Flex direction="row" gap={6} align="center" justify="center" width="100%">
      <ReportSummaryCircle
        totalErrors={totalErrors}
        plagiarism={plagiarismHelper}
        color="whiteOnRed"
        multiplier={isSmallerFoldHeight ? 4 : 6}
      />
      <Text variant="text-medium" as="p">
        <PlagiarismChartDescriptionCopy totalErrors={totalErrors} plagiarism={plagiarismHelper} />
      </Text>
    </Flex>
  )
}

export const PlagiarismCheckerOutput: React.FC<PlagiarismCheckerOutputProps> = ({ response }) => {
  const isInitial = response === undefined

  const errors: FeatureErrorsType[] = parseAlerts(response)

  const isSmallerFoldHeight = useMediaQuery(`(max-height: ${smallFoldHeight}px)`)

  return (
    <Flex
      direction="column"
      gap={isSmallerFoldHeight ? 1 : 4}
      align="start"
      justify="center"
      width="100%"
      height="100%"
      paddingLeft={6}
      paddingRight={6}
      paddingBottom={0}
      paddingTop={0}
    >
      <PlagiarismTitle isInitial={isInitial} errors={errors} isSmallerFoldHeight={isSmallerFoldHeight} />
      <ul className={style.reportList}>
        {errors.map((error) => (
          <Feature key={error.group} error={error} isInitial={isInitial} />
        ))}
      </ul>
    </Flex>
  )
}
